import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ChatbotTrainingExamplesPageSize } from '../../../types/userConfig';
import {
  buildTrainingExamplesListOptions,
  buildTrainingExamplesSearchParams,
  parseTrainingExamplesPageFromSearchParams,
  TRAINING_EXAMPLES_SEARCH_DEBOUNCE_MS,
  TRAINING_EXAMPLES_SEARCH_MIN_LENGTH,
  type TrainingExamplesSearchState,
} from '../chatbotTrainingExamplesListParams';

export function useChatbotTrainingExamplesListSearchParams(
  pageSize: ChatbotTrainingExamplesPageSize,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchDebounceTimeoutRef = useRef<number | null>(null);

  const urlSearchValue = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearchValue);
  const [previousUrlSearchValue, setPreviousUrlSearchValue] =
    useState(urlSearchValue);

  if (urlSearchValue !== previousUrlSearchValue) {
    setPreviousUrlSearchValue(urlSearchValue);
    setSearchInput(urlSearchValue);
  }

  const page = useMemo(
    () => parseTrainingExamplesPageFromSearchParams(searchParams),
    [searchParams],
  );

  const trainingSearch = useMemo((): TrainingExamplesSearchState => {
    const trimmed = urlSearchValue.trim();
    return {
      debouncedQuery:
        trimmed.length >= TRAINING_EXAMPLES_SEARCH_MIN_LENGTH ? trimmed : '',
    };
  }, [urlSearchValue]);

  useEffect(() => {
    return () => {
      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
      }
    };
  }, []);

  const replaceParams = useCallback(
    ({
      page: nextPage,
      searchInputValue,
    }: {
      page: number;
      searchInputValue: string;
    }) => {
      setSearchParams(
        buildTrainingExamplesSearchParams({
          page: nextPage,
          searchInput: searchInputValue,
        }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);

      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
        searchDebounceTimeoutRef.current = null;
      }

      if (value.trim() === '') {
        replaceParams({
          page: 1,
          searchInputValue: '',
        });
        return;
      }

      searchDebounceTimeoutRef.current = window.setTimeout(() => {
        replaceParams({
          page: 1,
          searchInputValue: value,
        });
        searchDebounceTimeoutRef.current = null;
      }, TRAINING_EXAMPLES_SEARCH_DEBOUNCE_MS);
    },
    [replaceParams],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      replaceParams({
        page: nextPage,
        searchInputValue: searchInput,
      });
    },
    [replaceParams, searchInput],
  );

  const listOptions = useMemo(
    () => buildTrainingExamplesListOptions(trainingSearch, page, pageSize),
    [page, pageSize, trainingSearch],
  );

  const hasActiveTrainingSearch =
    trainingSearch.debouncedQuery.length >= TRAINING_EXAMPLES_SEARCH_MIN_LENGTH;

  return {
    page,
    setPage,
    searchInput,
    listOptions,
    hasActiveTrainingSearch,
    handleSearchChange,
  };
}
